/** Semantic palette — light mode source values (see src/styles/tokens.css). */
export const palette = {
  error: "#c42034",
  success: "#00824e",
  warn: "#b07000",
  reject: "#c03030",
  cache: "#1a6fd0",
  cache2: "#3d8fe8",
  control: "#6030b8",
  accent: "#008878",
  accent2: "#00a892",
  title: "#1a2232",
  label: "#4a5f78",
  stroke: "#b8c4d8",
  marker: "#8090a8",
  navy: "#0c1f3f",
  navy2: "#142a52",
  navy3: "#1c3464",
  white: "#ffffff",
} as const;

/** Dark-mode semantic values — brightened for contrast on navy surfaces. */
export const paletteDark = {
  error: "#f06070",
  success: "#22c07a",
  warn: "#e8b040",
  reject: "#f07070",
  cache: "#5aabf5",
  cache2: "#82beff",
  control: "#a880f0",
  accent: "#30c8b0",
  accent2: "#48dcc4",
  title: "#eef2f8",
  label: "#a8b8cc",
  stroke: "#2e4568",
  marker: "#94a4b8",
  card: "#142a52",
  canvas: "#0c1f3f",
  navy2: "#142a52",
  surface: "#102444",
} as const;

export const chartPalette = {
  light: {
    tick: palette.label,
    grid: "rgba(184,196,216,0.6)",
    tooltipBg: palette.navy,
    tooltipBody: "#eef2f8",
    sliceBorder: palette.white,
    pointFill: palette.white,
  },
  dark: {
    tick: paletteDark.label,
    grid: "rgba(46,69,104,0.65)",
    tooltipBg: paletteDark.navy2,
    tooltipBody: paletteDark.title,
    sliceBorder: paletteDark.surface,
    pointFill: paletteDark.surface,
  },
} as const;

export const tierColors = {
  enterprise: palette.control,
  pro: palette.cache,
  basic: palette.accent,
} as const;

export const chartSeries = {
  light: [palette.control, palette.cache, palette.accent] as const,
  dark: [paletteDark.control, paletteDark.cache, paletteDark.accent] as const,
};

export function performanceRingColor(rate: number): string {
  if (rate >= 90) return palette.success;
  if (rate >= 75) return palette.warn;
  return palette.error;
}

/**
 * Single source of truth for the six brand stat-card accents. Each entry is the
 * CSS custom property that defines the accent, so light/dark and any brand
 * re-tune flow from the token layer — no parallel hex maps to drift.
 * `cssVar` → use as `rgb(var(--color-brand-blue))`; `text`/`bg` → Tailwind class
 * roots already wired to the same tokens (see tailwind.config.ts). [audit Color]
 */
export type StatAccent = "blue" | "green" | "teal" | "indigo" | "amber" | "rose";

export const accentPalette: Record<
  StatAccent,
  { cssVar: string; text: string; bg: string; gradientFrom: string; gradientTo: string }
> = {
  blue: { cssVar: "--color-primary", text: "text-primary", bg: "bg-primary/10", gradientFrom: "--color-primary", gradientTo: "--color-primary-2" },
  green: { cssVar: "--color-success", text: "text-green", bg: "bg-green/10", gradientFrom: "--color-success", gradientTo: "--color-success" },
  teal: { cssVar: "--color-accent", text: "text-mint", bg: "bg-mint/10", gradientFrom: "--color-accent", gradientTo: "--color-accent-2" },
  indigo: { cssVar: "--color-control", text: "text-indigo", bg: "bg-indigo/10", gradientFrom: "--color-control", gradientTo: "--color-primary" },
  amber: { cssVar: "--color-warn", text: "text-amber", bg: "bg-amber/10", gradientFrom: "--color-warn", gradientTo: "--color-warn" },
  rose: { cssVar: "--color-error", text: "text-rose", bg: "bg-rose/10", gradientFrom: "--color-error", gradientTo: "--color-error" },
};

/** Resolve an accent to a live `rgb(var(--token))` string (theme-aware). */
export function accentColor(accent: StatAccent): string {
  return `rgb(var(${accentPalette[accent].cssVar}))`;
}

/**
 * Token-backed default colors for chart slices/series, replacing the raw hex
 * literals previously inlined in chart examples so dark mode and brand re-tunes
 * track. Order: primary(blue), amber, red, teal, indigo. [audit Color #2]
 */
export const chartDefaultColors = [
  "rgb(var(--color-primary))",
  "rgb(var(--color-warn))",
  "rgb(var(--color-error))",
  "rgb(var(--color-accent))",
  "rgb(var(--color-control))",
] as const;
